// Odontogram geometry — tooth silhouettes and the dental arch.
//
// Pure maths, no React, so it is testable without a DOM.
//
// ── THE VIEW ───────────────────────────────────────────────────────────────
// Every tooth is drawn from the OCCLUSAL view (looking down onto the chewing
// surface), which is what a paper odontogram shows, in a LOCAL frame:
//
//        facial (buccal/labial — cheek or lip)      -y
//                     ▲
//        mesial ◄──── ● ────► distal        (before quadrant mirroring)
//                     ▼
//        lingual (tongue / palate)                  +y
//
// The tooth is drawn once in that frame and then ROTATED onto the arch. That
// is why the surfaces stay anatomically right all the way round the curve:
// mesial keeps pointing along the arch toward the midline, buccal keeps
// pointing out of the mouth, at every position. Laying flat boxes in a row and
// hand-flipping their labels per quadrant is what produces charts where half
// the surfaces are mislabelled.
//
// ── CROWN DIMENSIONS ───────────────────────────────────────────────────────
// Proportional to real mesiodistal × buccolingual crown widths (mm), so a
// molar reads as a molar at a glance. Scaled, not literal.

const MM = 3.1;   // px per mm — the whole chart scales from this one number

/** [mesiodistal, buccolingual] crown width in mm, by tooth type. */
const CROWN_MM = {
  centralIncisorUpper: [8.5, 7.0],
  lateralIncisorUpper: [6.5, 6.0],
  centralIncisorLower: [5.3, 6.0],
  lateralIncisorLower: [5.7, 6.5],
  canine: [7.6, 8.0],
  firstPremolar: [7.1, 9.2],
  secondPremolar: [6.6, 9.0],
  firstMolar: [10.3, 11.0],
  secondMolar: [9.8, 10.7],
  thirdMolar: [8.9, 10.0],
  // Primary crowns are markedly smaller — a child's chart must not look like
  // an adult's with different numbers.
  // Split, because one 'primary incisor' size collided with the permanent
  // lateral it succeeds — and a primary tooth is never wider than the
  // anterior that replaces it.
  primaryCentralIncisor: [6.5, 5.0],
  primaryLateralIncisor: [5.1, 4.1],
  primaryCanine: [6.0, 6.5],
  primaryFirstMolar: [7.3, 8.5],
  primarySecondMolar: [8.7, 9.5],
};

/** Tooth type from an FDI number — the shape family, not the name. */
export function toothType(fdi) {
  const q = Number(String(fdi)[0]);
  const p = Number(String(fdi)[1]);
  const primary = q >= 5;
  const upper = [1, 2, 5, 6].includes(q);

  if (primary) {
    if (p === 1) return 'primaryCentralIncisor';
    if (p === 2) return 'primaryLateralIncisor';
    if (p === 3) return 'primaryCanine';
    return p === 4 ? 'primaryFirstMolar' : 'primarySecondMolar';
  }
  if (p === 1) return upper ? 'centralIncisorUpper' : 'centralIncisorLower';
  if (p === 2) return upper ? 'lateralIncisorUpper' : 'lateralIncisorLower';
  if (p === 3) return 'canine';
  if (p === 4) return 'firstPremolar';
  if (p === 5) return 'secondPremolar';
  if (p === 6) return 'firstMolar';
  if (p === 7) return 'secondMolar';
  return 'thirdMolar';
}

export function crownSize(fdi) {
  const [md, bl] = CROWN_MM[toothType(fdi)] || CROWN_MM.firstMolar;
  return { w: md * MM, h: bl * MM };
}

export const isIncisorType = (t) => t.includes('ncisor');
export const isCanineType = (t) => t.includes('anine');
export const isMolarType = (t) => t.includes('olar') && !t.includes('remolar');
export const isPremolarType = (t) => t.includes('remolar');

/**
 * The crown outline, centred on (0,0) in the local frame.
 *
 * Each family gets a genuinely different silhouette — that is the whole point
 * of the shape work: a dentist should recognise a molar from its outline
 * without reading the number.
 *
 *   incisor    a blade: flat facial edge, tapering lingually
 *   canine     pointed — the cusp tip juts facially
 *   premolar   oval, waisted between two cusps
 *   molar      broad rounded rectangle
 */
export function crownPath(fdi) {
  return crownOutline(fdi, 1);
}

/**
 * The same silhouette at a scale — shared by `crownPath` (scale 1) and
 * `crownRingPath` (the روکش ring, drawn just inside it). One builder, so the
 * ring can never drift out of step with the outline it hugs.
 */
function crownOutline(fdi, scale) {
  const { w, h } = crownSize(fdi);
  const x = (w / 2) * scale;
  const y = (h / 2) * scale;
  const type = toothType(fdi);

  if (isIncisorType(type)) {
    // Flat facial edge (the incisal ridge seen from above), rounded lingually.
    return [
      `M ${-x} ${-y}`,
      `L ${x} ${-y}`,
      `Q ${x * 1.05} ${0} ${x * 0.72} ${y * 0.9}`,
      `Q ${0} ${y * 1.15} ${-x * 0.72} ${y * 0.9}`,
      `Q ${-x * 1.05} ${0} ${-x} ${-y}`,
      'Z',
    ].join(' ');
  }

  if (isCanineType(type)) {
    // The cusp tip points facially — the canine's defining feature.
    return [
      `M ${0} ${-y * 1.18}`,
      `Q ${x * 0.9} ${-y * 0.75} ${x * 0.95} ${0}`,
      `Q ${x * 0.85} ${y * 0.95} ${0} ${y}`,
      `Q ${-x * 0.85} ${y * 0.95} ${-x * 0.95} ${0}`,
      `Q ${-x * 0.9} ${-y * 0.75} ${0} ${-y * 1.18}`,
      'Z',
    ].join(' ');
  }

  if (isPremolarType(type)) {
    // Oval, slightly waisted at the mid — two cusps, buccal and lingual.
    return [
      `M ${0} ${-y}`,
      `Q ${x} ${-y * 0.8} ${x * 0.92} ${0}`,
      `Q ${x} ${y * 0.8} ${0} ${y}`,
      `Q ${-x} ${y * 0.8} ${-x * 0.92} ${0}`,
      `Q ${-x} ${-y * 0.8} ${0} ${-y}`,
      'Z',
    ].join(' ');
  }

  // Molar: broad, rounded rectangle.
  const r = Math.min(w, h) * 0.22 * scale;
  return [
    `M ${-x + r} ${-y}`,
    `L ${x - r} ${-y}`, `Q ${x} ${-y} ${x} ${-y + r}`,
    `L ${x} ${y - r}`, `Q ${x} ${y} ${x - r} ${y}`,
    `L ${-x + r} ${y}`, `Q ${-x} ${y} ${-x} ${y - r}`,
    `L ${-x} ${-y + r}`, `Q ${-x} ${-y} ${-x + r} ${-y}`,
    'Z',
  ].join(' ');
}

/**
 * The five surface regions, in the local frame.
 *
 * Topology is the charting box every dentist knows — four peripheral regions
 * around a central one — but sized to THIS tooth and clipped to its outline,
 * so the familiar hit targets survive the anatomical silhouette.
 *
 * `centre` is occlusal on posteriors and incisal on anteriors; the caller
 * supplies which, because that is a data rule (app/domain/teeth.py), not a
 * geometric one.
 */
export function surfaceRegions(fdi) {
  const { w, h } = crownSize(fdi);
  const x = w / 2;
  const y = h / 2;
  // The central region is proportionally LARGER on molars: the occlusal table
  // really is most of a molar's crown, and it is the most-clicked surface.
  const inset = isMolarType(toothType(fdi)) ? 0.34 : 0.30;
  const ix = x * (1 - inset * 2) + x * inset * 0.55;
  const iy = y * (1 - inset * 2) + y * inset * 0.55;

  return {
    // facial (buccal/labial) — always toward -y in the local frame
    facial: `${-x},${-y} ${x},${-y} ${ix},${-iy} ${-ix},${-iy}`,
    lingual: `${-x},${y} ${x},${y} ${ix},${iy} ${-ix},${iy}`,
    // left/right in the local frame; the caller maps them to mesial/distal,
    // because WHICH side is mesial depends on the quadrant.
    left: `${-x},${-y} ${-ix},${-iy} ${-ix},${iy} ${-x},${y}`,
    right: `${x},${-y} ${ix},${-iy} ${ix},${iy} ${x},${y}`,
    centre: { x: -ix, y: -iy, w: ix * 2, h: iy * 2 },
  };
}

/** Decorative cusp marks inside the occlusal table. Never interactive — they
 *  exist so a molar reads as a molar, and must not intercept a surface click. */
export function cusps(fdi) {
  const type = toothType(fdi);
  const { w, h } = crownSize(fdi);
  const x = w * 0.19;
  const y = h * 0.19;
  const r = Math.min(w, h) * 0.055;

  if (isMolarType(type)) {
    const pts = [[-x, -y], [x, -y], [-x, y], [x, y]];
    // First molars carry a fifth cusp (the distolingual/Carabelli region) —
    // the classic way a chart distinguishes a 6 from a 7 at a glance.
    if (type === 'firstMolar' || type === 'primarySecondMolar') pts.push([x * 1.35, y * 0.25]);
    return pts.map(([cx, cy]) => ({ cx, cy, r }));
  }
  if (isPremolarType(type)) return [{ cx: 0, cy: -y, r }, { cx: 0, cy: y, r }];
  if (isCanineType(type)) return [{ cx: 0, cy: -h * 0.14, r: r * 1.1 }];
  return [];   // incisors have an edge, not cusps
}

// ── Fissures ───────────────────────────────────────────────────────────────
//
// The groove pattern of the chewing surface — what makes an occlusal table
// read as one instead of a blank plate. Decorative only: always rendered
// pointerEvents:none at low opacity, never a hit target.

/**
 * The central fissure, in the local frame, scaled from `crownSize`.
 *
 *   molar      an H: two buccolingual grooves joined by the central
 *              mesiodistal groove — the pattern runs BETWEEN the cusps,
 *              so it shares the cusp grid's 0.19 spacing
 *   premolar   the single central (mesiodistal) groove between its two cusps
 *   anterior   the incisal edge seen from above — a subtle line just inside
 *              the facial edge
 */
export function fissurePath(fdi) {
  const type = toothType(fdi);
  const { w, h } = crownSize(fdi);
  const x = w * 0.19;   // the cusp grid — fissures run between cusps
  const y = h * 0.19;

  if (isMolarType(type)) {
    return [
      `M ${-x} ${-y * 1.3} L ${-x} ${y * 1.3}`,
      `M ${x} ${-y * 1.3} L ${x} ${y * 1.3}`,
      `M ${-x * 1.5} 0 L ${x * 1.5} 0`,
    ].join(' ');
  }
  if (isPremolarType(type)) {
    return `M ${-w * 0.27} 0 L ${w * 0.27} 0`;
  }
  // Anteriors (incisors and canines): the incisal edge line.
  return `M ${-w * 0.3} ${-h * 0.34} L ${w * 0.3} ${-h * 0.34}`;
}

// ── Condition glyphs ───────────────────────────────────────────────────────
//
// The printed-chart symbols for whole-tooth conditions. All are pure
// generators in the LOCAL frame (facial toward -y) so they rotate with the
// tooth, all derive from `crownSize` so they scale with it, and all are
// rendered pointerEvents:none. SHAPE carries the meaning — colour only
// reinforces it — because a chart must survive colour-blindness.

/** روکش — a second outline just inside the silhouette, the universal crown
 *  convention. Same builder as `crownPath`, scaled in. */
export function crownRingPath(fdi) {
  return crownOutline(fdi, 0.76);
}

/** عصب‌کشی — a small filled triangle at the crown CENTRE (the pulp position).
 *  "Apically" cannot be shown from the occlusal view — the apex is along the
 *  tooth axis, perpendicular to this plane — so the printed convention is a
 *  mark in the pulp position, which is the centre. Returns polygon points. */
export function rootCanalGlyph(fdi) {
  const { w, h } = crownSize(fdi);
  const s = Math.min(w, h) * 0.13;
  return `0,${s} ${-s * 0.9},${-s * 0.75} ${s * 0.9},${-s * 0.75}`;
}

/** ایمپلنت — the screw convention: three short horizontal bars stacked at the
 *  centre, tapering like a thread. Returns line segments. */
export function implantGlyph(fdi) {
  const { w, h } = crownSize(fdi);
  const m = Math.min(w, h);
  const gap = m * 0.13;
  return [
    { x1: -m * 0.17, y1: -gap, x2: m * 0.17, y2: -gap },
    { x1: -m * 0.14, y1: 0, x2: m * 0.14, y2: 0 },
    { x1: -m * 0.11, y1: gap, x2: m * 0.11, y2: gap },
  ];
}

/** پست و کور — a vertical bar (the post) with a dot at its head (the core). */
export function postCoreGlyph(fdi) {
  const { w, h } = crownSize(fdi);
  const m = Math.min(w, h);
  return {
    bar: { x1: 0, y1: -m * 0.2, x2: 0, y2: m * 0.16 },
    dot: { cx: 0, cy: -m * 0.2, r: m * 0.075 },
  };
}

/** ونیر — a thickened arc hugging the FACIAL edge only (-y in the local
 *  frame): a veneer is a facial shell, and the glyph says exactly that. */
export function veneerArcPath(fdi) {
  const { w, h } = crownSize(fdi);
  const x = w / 2;
  const y = h / 2;
  return `M ${-x * 0.62} ${-y * 0.55} Q 0 ${-y * 1.02} ${x * 0.62} ${-y * 0.55}`;
}

/** شکستگی — a zigzag across the crown, roughly corner to corner. Returns
 *  polyline points. The end points sit ON the diagonal (no jag) so the crack
 *  visibly enters and exits the crown. */
export function fractureGlyph(fdi) {
  const { w, h } = crownSize(fdi);
  const x = w / 2;
  const y = h / 2;
  const SEGMENTS = 5;
  const pts = [];
  for (let i = 0; i <= SEGMENTS; i += 1) {
    const t = i / SEGMENTS;
    const jag = (i === 0 || i === SEGMENTS) ? 0 : (i % 2 ? 1 : -1);
    pts.push(`${-x * 0.6 + x * 1.2 * t},${-y * 0.45 + y * 0.9 * t + jag * y * 0.2}`);
  }
  return pts.join(' ');
}

// ── The arch ───────────────────────────────────────────────────────────────
//
// Teeth sit on an ellipse arc rather than a straight line, because that is the
// shape of a jaw and the shape of every printed odontogram — including the
// reference chart this was drawn from. It is not decoration: the curve is what
// lets each tooth ROTATE so its buccal surface faces out of the mouth, which
// is what keeps the surface labels honest all the way to the third molars.

/**
 * Position and rotation for the i-th of n teeth on an arch.
 *
 * @param upper  upper arch bows up ("∩"), lower bows down ("∪")
 * @returns {{x, y, rotation}} rotation in DEGREES, for an SVG transform
 */
export function archPlacement(i, n, { cx, cy, rx, ry, upper }) {
  // Spread across the arc, insetting the ends so the last tooth is not exactly
  // on the horizontal axis (where the rotation would be a full 90°).
  const t = n === 1 ? 0.5 : i / (n - 1);
  return placeAtTheta(Math.PI * (0.06 + t * 0.88), { cx, cy, rx, ry, upper });
}

/** Position + rotation at a given ellipse parameter. */
function placeAtTheta(theta, { cx, cy, rx, ry, upper }) {
  const x = cx - rx * Math.cos(theta);
  const y = upper ? cy - ry * Math.sin(theta) : cy + ry * Math.sin(theta);

  // Outward normal — away from the arch centre, i.e. out of the mouth.
  const nx = -rx * Math.cos(theta);
  const ny = upper ? -ry * Math.sin(theta) : ry * Math.sin(theta);

  // The local frame draws facial toward -y, so rotate that onto the normal.
  const rotation = (Math.atan2(ny, nx) * 180) / Math.PI + 90;
  return { x, y, rotation };
}

// ── Arc-length spacing ─────────────────────────────────────────────────────
//
// WHY THE ANGLE PARAMETER IS NOT ENOUGH — the bug this replaces.
//
// Stepping θ evenly does NOT step evenly along an ellipse. The speed is
//     |d/dθ| = sqrt(rx²sin²θ + ry²cos²θ)
// which for the chart's arch (rx≈232, ry≈96) is ~96 at the ends and ~232 at
// the apex: the ends advance 2.4× slower, so teeth bunch there. And the ends
// are exactly where the MOLARS are — the widest teeth got the least room, and
// 15/16/17/18 visibly overlapped each other on screen.
//
// The fix is to walk ARC LENGTH and give each tooth room proportional to its
// own mesiodistal crown width, which is also what a real arch does: teeth sit
// contiguously, a molar occupying more of the curve than an incisor.

const ARC_STEPS = 720;

/** Cumulative arc length along the arch, sampled once per arch. */
function arcTable({ rx, ry }, theta0, theta1) {
  const table = [{ theta: theta0, s: 0 }];
  let s = 0;
  const dt = (theta1 - theta0) / ARC_STEPS;
  for (let k = 1; k <= ARC_STEPS; k += 1) {
    const a = theta0 + dt * (k - 1);
    const b = theta0 + dt * k;
    // Trapezoid on the speed function — plenty at 720 samples, and it runs
    // once per arch per render rather than per tooth.
    const sa = Math.hypot(rx * Math.sin(a), ry * Math.cos(a));
    const sb = Math.hypot(rx * Math.sin(b), ry * Math.cos(b));
    s += ((sa + sb) / 2) * dt;
    table.push({ theta: b, s });
  }
  return table;
}

/** Invert the table: the θ at a given arc length. */
function thetaAt(table, s) {
  const last = table[table.length - 1];
  if (s <= 0) return table[0].theta;
  if (s >= last.s) return last.theta;
  let lo = 0;
  let hi = table.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (table[mid].s <= s) lo = mid; else hi = mid;
  }
  const span = table[hi].s - table[lo].s;
  const f = span > 0 ? (s - table[lo].s) / span : 0;
  return table[lo].theta + f * (table[hi].theta - table[lo].theta);
}

/**
 * Place a row of teeth along the arch, each occupying arc proportional to its
 * OWN crown width, so nothing overlaps and the molars get the room they need.
 *
 * If the teeth are wider than the arch, a `scale` < 1 is returned and the
 * CALLER must apply it to the crown as well. Scaling only the spacing would
 * shrink the gaps while leaving the teeth full size — which is the overlap
 * this function exists to prevent, arrived at from the other direction.
 *
 * @param fdis  tooth numbers in DRAWING order (screen left → right)
 */
export function archPlacements(fdis, opts) {
  const theta0 = Math.PI * 0.04;
  const theta1 = Math.PI * 0.96;
  const table = arcTable(opts, theta0, theta1);
  const total = table[table.length - 1].s;

  const box = fdis.map((fdi) => {
    const { w, h } = crownSize(fdi);
    return { halfW: w / 2, halfH: h / 2 };
  });

  // First pass at natural spacing, then GROW any gap whose teeth actually
  // collide (see needSeparation). Two passes: the separation test needs
  // positions, positions need the spacing.
  // The arch midpoint IS the dental midline: the arc is symmetric about it, so
  // growing outward from there puts the central incisors either side of the
  // chart's dashed axis by construction.
  const centre = total / 2;
  let scale = 1;
  let placed = layout(fdis, box, table, total, opts, scale, centre);
  const overflow = placed.usedArc / total;
  if (overflow > 1) {
    // Too long even after separation — shrink everything, CROWNS INCLUDED
    // (the renderer applies `scale`), and lay out once more at the smaller
    // size. Shrinking only the spacing would reintroduce the overlap.
    scale = 1 / overflow;
    placed = layout(fdis, box, table, total, opts, scale, centre);
  }
  return placed.teeth;
}

/**
 * One layout pass, run OUTWARD FROM THE MIDLINE.
 *
 * Not left-to-right: the separation pass pushes each tooth away from its
 * neighbour, so a single sweep accumulates all of that slack at one end and the
 * two central incisors stop straddling the midline (measured 6px off). The
 * midline is the anatomical reference — it is where the chart's dashed axis is
 * drawn and where symmetry is judged — so the layout starts there and grows
 * both ways, which makes symmetry automatic instead of approximate.
 */
function layout(fdis, box, table, total, opts, scale, centreArc) {
  const MIN_GAP = 2.5;
  const n = fdis.length;
  const mid = Math.floor(n / 2);          // first tooth LEFT of the midline
  const teeth = new Array(n);

  const make = (i, arc) => ({
    fdi: fdis[i], arc, scale,
    halfW: box[i].halfW * scale,
    halfH: box[i].halfH * scale,
    ...placeAtTheta(thetaAt(table, arc), opts),
  });

  // The two teeth either side of the midline. They are the ONLY pair not
  // produced by the outward growth loop, so they need the same corner test
  // applied explicitly — seeding them from half-widths alone left the central
  // incisors 0.03px apart while every other pair had 2.7px, because a rotated
  // crown reaches to its CORNER, not its mesiodistal edge.
  //
  // Both are pushed out by the same amount, so the midline stays exact.
  let half = MIN_GAP / 2;
  for (let guard = 0; guard < 60; guard += 1) {
    if (mid > 0) teeth[mid - 1] = make(mid - 1, centreArc - half - box[mid - 1].halfW * scale);
    if (mid < n) teeth[mid] = make(mid, centreArc + half + box[mid].halfW * scale);
    if (mid === 0 || mid >= n) break;
    if (clears(teeth[mid - 1], teeth[mid], MIN_GAP)) break;
    half += 0.75;
  }

  // Grow outward, pushing each tooth until its corners clear its inward
  // neighbour's (see clears() — reach is the ROTATED extent, not the width).
  const grow = (from, step) => {
    for (let i = from; i >= 0 && i < n; i += step) {
      const prev = teeth[i - step];
      let advance = prev.halfW + box[i].halfW * scale + MIN_GAP;
      let candidate = make(i, prev.arc + step * advance);
      for (let guard = 0; guard < 60 && !clears(prev, candidate, MIN_GAP); guard += 1) {
        advance += 1.5;
        candidate = make(i, prev.arc + step * advance);
      }
      teeth[i] = candidate;
    }
  };
  grow(mid + 1, 1);
  grow(mid - 2, -1);

  // How much arc the finished run needs, measured tip to tip.
  const first = teeth[0];
  const last = teeth[n - 1];
  const usedArc = (last.arc + last.halfW) - (first.arc - first.halfW);

  return { teeth, usedArc };
}

/**
 * Do two placed crowns clear each other?
 *
 * The separating-axis test along the line joining their centres. A rotated
 * rectangle's reach along a direction is
 *     halfW·|x̂·u| + halfH·|ŷ·u|
 * where x̂/ŷ are the tooth's own axes — which is why sizing by mesiodistal
 * width alone was wrong: a rotated molar presents its CORNER (reach ≈ the
 * half-diagonal, ~23px, not ~16px), and at the arch ends neighbouring teeth
 * differ sharply in rotation, so their corners swing into one another. That is
 * the overlap that survived the first arc-length fix.
 */
function clears(a, b, margin) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy);
  if (len === 0) return false;
  const ux = dx / len;
  const uy = dy / len;
  return len >= reachAlong(a, ux, uy) + reachAlong(b, ux, uy) + margin;
}

function reachAlong(tooth, ux, uy) {
  const r = (tooth.rotation * Math.PI) / 180;
  const cos = Math.cos(r);
  const sin = Math.sin(r);
  // Tooth axes in world space: local +x → (cos, sin), local +y → (-sin, cos).
  return tooth.halfW * Math.abs(cos * ux + sin * uy)
       + tooth.halfH * Math.abs(-sin * ux + cos * uy);
}

/** Bounding box of an arch's teeth, so the caller can size the viewBox. */
export function archExtent(n, opts, fdis) {
  let minX = Infinity; let maxX = -Infinity;
  let minY = Infinity; let maxY = -Infinity;
  for (let i = 0; i < n; i += 1) {
    const { x, y } = archPlacement(i, n, opts);
    const { w, h } = crownSize(fdis[i]);
    const reach = Math.max(w, h) * 0.75;   // rotation-safe half-diagonal
    minX = Math.min(minX, x - reach); maxX = Math.max(maxX, x + reach);
    minY = Math.min(minY, y - reach); maxY = Math.max(maxY, y + reach);
  }
  return { minX, maxX, minY, maxY };
}
